import React, { useState } from 'react'
import { DatePicker, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import moment, { Moment } from 'moment'
import Button from '@/components/button/button'
import SearchInput from '@/components/search-input/search-input'
import $msg from '@/utils/message'
import { layerManage, showLayer } from '@/utils/layer'
import { getMemberUserBindingRecords } from '../api'
import MemberRecordDetail from '../member-record-detail/member-record-detail'
import SettingPage, { SettingTableContainer } from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'

const MemberBindingRecord: React.FC = () => {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [keyword, setKeyword] = useState('')
    const [searchKey, setSearchKey] = useState(0)
    const [date, setDate] = useState<string[]>([
        dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
    ])
    const [dateValue, setDateValue] = useState<[Moment | null, Moment | null] | null>([
        moment().subtract(1, 'month'),
        moment(),
    ])
    const resetFilter = () => {
        setPage(1)
        setKeyword('')
        setSearchKey(value => value + 1)
        setDate([dayjs().subtract(1, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])
        setDateValue([moment().subtract(1, 'month'), moment()])
    }
    const req = useRequest(
        () =>
            getMemberUserBindingRecords({
                page,
                limit: pageSize,
                keyword: keyword || undefined,
                from_date: date[0],
                to_date: date[1],
            }),
        {
            refreshDeps: [page, pageSize, keyword, date],
            onError: e => $msg('error', e.message || '获取绑定记录失败'),
        },
    )
    const showRecordDetail = (record: any) => {
        showLayer(
            <MemberRecordDetail
                title="绑定记录详情"
                onClose={() => layerManage.pop()}
                sections={[
                    {
                        title: '绑定信息',
                        items: [
                            {
                                label: '绑定时间',
                                value: record.create_time
                                    ? dayjs(record.create_time).format('YYYY-MM-DD HH:mm:ss')
                                    : '--',
                            },
                            { label: '绑定前姓名', value: record.name || '--' },
                            { label: '绑定前手机号', value: record.phone || '--' },
                            {
                                label: '绑定后会员',
                                value: record.membership?.name || record.membership?.user?.nickname || '--',
                            },
                            { label: '绑定后手机号', value: record.membership?.phone || '--' },
                            {
                                label: '绑定用户',
                                value: record.user?.nickname || record.user?.realname || '--',
                            },
                        ],
                    },
                    {
                        title: '会员资产',
                        items: [
                            { label: '余额', value: record.balance ?? '--' },
                            { label: '积分', value: record.points ?? '--' },
                            { label: '成长值', value: record.experience ?? '--' },
                        ],
                    },
                ]}
            />,
        )
    }
    const columns: ColumnsType<any> = [
        {
            title: '时间',
            dataIndex: 'create_time',
            width: 170,
            render: value => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
        },
        { title: '绑定前姓名', dataIndex: 'name', width: 160, render: value => value || '--' },
        { title: '绑定前手机号', dataIndex: 'phone', width: 152, render: value => value || '--' },
        {
            title: '绑定后会员',
            dataIndex: 'membership',
            width: 168,
            render: value => value?.name || value?.user?.nickname || '--',
        },
        {
            title: '绑定用户',
            dataIndex: 'user',
            width: 168,
            render: value => value?.nickname || value?.realname || '--',
        },
        { title: '余额', dataIndex: 'balance', width: 112, render: value => value || 0 },
        { title: '积分', dataIndex: 'points', width: 96, render: value => value || 0 },
        {
            title: '操作',
            width: 88,
            fixed: 'right',
            render: (_value, record) => (
                <span className="action" onClick={() => showRecordDetail(record)}>
                    详情
                </span>
            ),
        },
    ]

    return (
        <SettingPage
            title="绑定记录"
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            extra={
                <>
                    <DatePicker.RangePicker
                        dateRender={current => (
                            <div className="ant-picker-cell-inner" style={{ borderRadius: '50%' }}>
                                {current.date()}
                            </div>
                        )}
                        value={dateValue}
                        allowClear
                        suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                        onChange={(momentValue, dateString) => {
                            setPage(1)
                            setDateValue(momentValue)
                            setDate(dateString[0] ? dateString : [])
                        }}
                    />
                    <div className="member-basic-setting__search">
                        <SearchInput
                            key={searchKey}
                            placeholder="输入会员姓名/手机号/昵称"
                            onSearch={val => {
                                setPage(1)
                                setKeyword(val)
                            }}
                            onDelete={() => {
                                setPage(1)
                                setKeyword('')
                            }}
                        />
                    </div>
                    <Button types="primary" onClick={resetFilter}>
                        重置
                    </Button>
                    <Button types="primary" loading={req.loading} onClick={() => req.refresh()}>
                        刷新
                    </Button>
                </>
            }
        >
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey={(record, index) => `${record.create_time}-${record.phone}-${index}`}
                    loading={req.loading}
                    columns={columns}
                    dataSource={req.data?.records || []}
                    pagination={getMemberTablePagination({
                        current: page,
                        pageSize,
                        total: getTableTotal(req.data?.pagination),
                        onChange: setPage,
                        onShowSizeChange: (nextPage, nextSize) => {
                            setPage(nextPage)
                            setPageSize(nextSize)
                        },
                    })}
                    scroll={{ x: 'max-content' }}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default MemberBindingRecord
